import React from "react"
import Icon from "../icons/icon"
import classNames from "../utils/classnames"

interface ISocialShareIconsProps {
  circle?: boolean
  withText?: boolean
  stacked?: boolean
  bgClass?: string
  uri: string
  message: string
  className?: string
  icons: Array<
    | `facebook`
    | `twitter`
    | `tumblr`
    | `email`
    | `pinterest`
    | `linkedin`
    | `reddit`
    | `xing`
    | `whatsapp`
    | `hackernews`
    | `vk`
    | `telegram`
  >
}

function SocialShareIcons({
  circle = true,
  withText = false,
  stacked = false,
  bgClass,
  uri,
  message,
  className,
  icons,
}: ISocialShareIconsProps): JSX.Element {
  return (
    <div
      className={classNames(
        `flex gap-2 lg:gap-0`,
        stacked ? `flex-row lg:flex-col` : `flex-row`
      )}
    >
      {/* Sharingbutton Facebook  */}
      {icons.includes(`facebook`) && (
        <a
          href={`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            uri
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on Facebook"
        >
          <div className={classNames(`inline-flex`)}>
            <div aria-hidden="true">
              <Icon
                className={classNames(
                  `text-white`,
                  bgClass
                    ? `${bgClass} hover:vivid-gradient`
                    : `bg-[#3b5998] hover:bg-[#2d4373]`,
                  className ? className : `w-8 h-8`,
                  circle ? `rounded-full` : `rounded-none`
                )}
                sourceSvgWidth={24}
                sourceSvgHeight={24}
              >
                {/* <circle cx="12" cy="12" r="11.5" /> */}
                <path d="M15.84 9.5H13.5V8.48c0-.53.35-.65.6-.65h1.4v-2.3h-2.35c-2.3 0-2.65 1.7-2.65 2.8V9.5h-2v2h2v7h3v-7h2.1l.24-2z" />
              </Icon>
            </div>
            {withText && `Share on Facebook`}
          </div>
        </a>
      )}

      {/* Sharingbutton Twitter  */}
      {icons.includes(`twitter`) && (
        <a
          href={`https://twitter.com/intent/tweet/?text=${encodeURIComponent(
            message
          )}&amp;url=${encodeURIComponent(uri)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on Twitter"
        >
          <div className={classNames(`inline-flex`)}>
            <div aria-hidden="true">
              <Icon
                className={classNames(
                  `text-white`,
                  bgClass
                    ? `${bgClass} hover:vivid-gradient`
                    : `bg-[#55acee] hover:bg-[#2795e9]`,
                  className ? className : `w-8 h-8`,
                  circle ? `rounded-full` : `rounded-none`
                )}
                sourceSvgWidth={24}
                sourceSvgHeight={24}
              >
                <path d="M18.5 7.4l-2 .2c-.4-.5-1-.8-2-.8C13.3 6.8 12 8 12 9.4v.6c-2 0-4-1-5.4-2.7-.2.4-.3.8-.3 1.3 0 1 .4 1.7 1.2 2.2-.5 0-1 0-1.2-.3 0 1.3 1 2.3 2 2.6-.3.4-.7.4-1 0 .2 1.4 1.2 2 2.3 2-1 1-2.5 1.4-4 1 1.3 1 2.7 1.4 4.2 1.4 4.8 0 7.5-4 7.5-7.5v-.4c.5-.4.8-1.5 1.2-2z" />
                {/* <circle cx="12" cy="12" r="11.5" /> */}
              </Icon>
            </div>
            {withText && `Share on Twitter`}
          </div>
        </a>
      )}

      {/* Sharingbutton Tumblr  */}
      {icons.includes(`tumblr`) && (
        <a
          href={`https://www.tumblr.com/widgets/share/tool?posttype=link&amp;title=${encodeURIComponent(
            message
          )}&amp;caption=${encodeURIComponent(
            message
          )}&amp;content=${encodeURIComponent(
            uri
          )}&amp;canonicalUrl=${encodeURIComponent(
            uri
          )}&amp;shareSource=tumblr_share_button`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on Tumblr"
        >
          <div className={classNames(`inline-flex`)}>
            <div aria-hidden="true">
              <Icon
                className={classNames(
                  `text-white`,
                  bgClass
                    ? `${bgClass} hover:vivid-gradient`
                    : `bg-[#35465C] hover:bg-[#222d3c]`,
                  className ? className : `w-8 h-8`,
                  circle ? `rounded-full` : `rounded-none`
                )}
                sourceSvgWidth={24}
                sourceSvgHeight={24}
              >
                {/* <circle cx="12" cy="12" r="11.5" /> */}
                <path d="M12.5 4.5v3h2v2h-2v3.72c0 2.47 1.48 2.7 3 1.7v2.7c-4.1 1.92-6-.62-6-3.6V9.5h-2V8.14c.55-.18 1.24-.43 1.63-.77.4-.33.7-.73.94-1.2.24-.46.4-.95.5-1.67h1.93z" />
              </Icon>
            </div>
            {withText && `Share on Tumblr`}
          </div>
        </a>
      )}

      {/* Sharingbutton E-Mail  */}
      {icons.includes(`email`) && (
        <a
          href={`mailto:?subject=${encodeURIComponent(
            uri
          )}&amp;body=${encodeURIComponent(message)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share by E-Mail"
        >
          <div className={classNames(`inline-flex`)}>
            <div aria-hidden="true">
              <Icon
                className={classNames(
                  `text-white`,
                  bgClass
                    ? `${bgClass} hover:vivid-gradient`
                    : `bg-[#777777] hover:bg-[#5e5e5e]`,
                  className ? className : `w-8 h-8`,
                  circle ? `rounded-full` : `rounded-none`
                )}
                sourceSvgWidth={24}
                sourceSvgHeight={24}
              >
                <path d="m5.05,7.02l0,10.54l14.05,0l0,-10.54l-14.05,0zm3.87,4.64l-2.7,3.34l0,-5.54l2.7,2.19zm-2.42,-3.47l11.14,0l-5.57,4.51l-5.57,-4.51zm3.34,4.21l2.24,1.81l2.24,-1.82l3.28,3.99l-10.99,0l3.23,-3.99zm5.38,-0.74l2.7,-2.19l0,5.47l-2.7,-3.28z" />
                {/* <circle cx="12" cy="12" r="11.5" /> */}
              </Icon>
            </div>
            {withText && `Share by E-Mail`}
          </div>
        </a>
      )}

      {/* Sharingbutton Pinterest  */}
      {icons.includes(`pinterest`) && (
        <a
          href={`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(
            uri
          )}&amp;media=${encodeURIComponent(
            uri
          )}&amp;description=${encodeURIComponent(message)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on Pinterest"
        >
          <div className={classNames(`inline-flex`)}>
            <div aria-hidden="true">
              <Icon
                className={classNames(
                  `text-white`,
                  bgClass
                    ? `${bgClass} hover:vivid-gradient`
                    : `bg-[#bd081c] hover:bg-[#8c0615]`,
                  className ? className : `w-8 h-8`,
                  circle ? `rounded-full` : `rounded-none`
                )}
                sourceSvgWidth={24}
                sourceSvgHeight={24}
              >
                {/* <circle cx="12" cy="12" r="11.5" /> */}
                <path d="M8 11.2c-.15-.32-.25-.72-.25-1.22 0-2.32 1.74-4.4 4.53-4.4 2.47 0 3.82 1.5 3.82 3.52 0 2.64-1.17 4.88-2.9 4.88-.97 0-1.7-.8-1.46-1.77.28-1.14.8-2.4.8-3.23 0-.76-.4-1.38-1.23-1.38-.95 0-1.74 1-1.74 2.37 0 .86.3 1.45.3 1.45l-1.2 5c-.34 1.5-.04 3.33-.02 3.5.02.1.16.15.22.06.1-.12 1.26-1.56 1.66-3l.66-2.53c.32.6 1.25 1.14 2.24 1.14 2.95 0 4.95-2.7 4.95-6.3 0-2.73-2.3-5.27-5.82-5.27-4.36 0-6.57 3.14-6.57 5.75 0 .85.18 1.64.53 2.28l1.5-.8z" />
              </Icon>
            </div>
            {withText && `Share on Pinterest`}
          </div>
        </a>
      )}

      {/* Sharingbutton LinkedIn  */}
      {icons.includes(`linkedin`) && (
        <a
          href={`https://www.linkedin.com/shareArticle?mini=true&amp;url=${encodeURIComponent(
            uri
          )}&amp;title=${encodeURIComponent(
            message
          )}&amp;summary=${encodeURIComponent(
            message
          )}&amp;source=${encodeURIComponent(uri)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on LinkedIn"
        >
          <div className={classNames(`inline-flex`)}>
            <div aria-hidden="true">
              <Icon
                className={classNames(
                  `text-white`,
                  bgClass
                    ? `${bgClass} hover:vivid-gradient`
                    : `bg-[#0077b5] hover:bg-[#046293]`,
                  className ? className : `w-8 h-8`,
                  circle ? `rounded-full` : `rounded-none`
                )}
                sourceSvgWidth={24}
                sourceSvgHeight={24}
              >
                {/* <circle cx="12" cy="12" r="11.5" /> */}
                <path d="M15 12.5c-.28 0-.5.22-.5.5v3.5h-3s.03-6.48 0-7h3v.83s.46-.75 1.7-.75c1.56 0 2.3 1.12 2.3 3.3v3.62h-3V13c0-.28-.23-.5-.5-.5zm-7.5-3h2v7h-2z" />
                <circle cx="8.5" cy="6.5" r="1" />
              </Icon>
            </div>
            {withText && `Share on LinkedIn`}
          </div>
        </a>
      )}

      {/* Sharingbutton Reddit  */}
      {icons.includes(`reddit`) && (
        <a
          href={`https://reddit.com/submit/?url=${encodeURIComponent(
            uri
          )}&amp;resubmit=true&amp;title=${encodeURIComponent(message)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on Reddit"
        >
          <div className={classNames(`inline-flex`)}>
            <div aria-hidden="true">
              <Icon
                className={classNames(
                  `text-white`,
                  bgClass
                    ? `${bgClass} hover:vivid-gradient`
                    : `bg-[#5f99cf] hover:bg-[#3a80c1]`,
                  className ? className : `w-8 h-8`,
                  circle ? `rounded-full` : `rounded-none`
                )}
                sourceSvgWidth={24}
                sourceSvgHeight={24}
              >
                {/* <circle cx="12" cy="12" r="11.5" /> */}
                <ellipse cx="12" cy="14.37" rx="6.2" ry="4.24" />
                <path d="M14.3 16.25c-.62.36-1.42.57-2.3.57-.88 0-1.7-.2-2.32-.58" />
                <circle cx="14.61" cy="13.39" r=".98" />
                <circle cx="9.39" cy="13.39" r=".98" />
                <path d="M16.4 11.38c.26-.55.82-.92 1.47-.92.9 0 1.63.73 1.63 1.63 0 .8-.6 1.47-1.38 1.6" />
                <circle cx="17.22" cy="7.52" r="1.63" />
                <path d="M7.6 11.38c-.26-.54-.82-.92-1.47-.92-.9 0-1.63.73-1.63 1.63 0 .8.6 1.47 1.38 1.6M12 10.12s-.08-4.82 3.6-2.6" />
              </Icon>
            </div>
            {withText && `Share on Reddit`}
          </div>
        </a>
      )}

      {/* Sharingbutton XING  */}
      {icons.includes(`xing`) && (
        <a
          href={`https://www.xing.com/app/user?op=share;url=${encodeURIComponent(
            uri
          )};title=${encodeURIComponent(message)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on XING"
        >
          <div className={classNames(`inline-flex`)}>
            <div aria-hidden="true">
              <Icon
                className={classNames(
                  `text-white`,
                  bgClass
                    ? `${bgClass} hover:vivid-gradient`
                    : `bg-[#1a7576] hover:bg-[#114C4C]`,
                  className ? className : `w-8 h-8`,
                  circle ? `rounded-full` : `rounded-none`
                )}
                sourceSvgWidth={24}
                sourceSvgHeight={24}
              >
                {/* <circle cx="12" cy="12" r="11.5" /> */}
                <path d="M8.4 8.5h-3L7 11.3l-2.2 3.2h3l2.3-3.2zm10-3h-3.2l-5 8.5 3.2 5.5h3.3l-3-5.5z" />
              </Icon>
            </div>
            {withText && `Share on XING`}
          </div>
        </a>
      )}

      {/* Sharingbutton WhatsApp  */}
      {icons.includes(`whatsapp`) && (
        <a
          href={`whatsapp://send?text=${encodeURIComponent(
            message
          )}%20${encodeURIComponent(uri)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on WhatsApp"
        >
          <div className={classNames(`inline-flex`)}>
            <div aria-hidden="true">
              <Icon
                className={classNames(
                  `text-white`,
                  bgClass
                    ? `${bgClass} hover:vivid-gradient`
                    : `bg-[#25D366] hover:bg-[#1DA851]`,
                  className ? className : `w-8 h-8`,
                  circle ? `rounded-full` : `rounded-none`
                )}
                sourceSvgWidth={24}
                sourceSvgHeight={24}
              >
                {/* <circle
                xmlns="http://www.w3.org/2000/svg"
                cx="12"
                cy="12"
                r="11.5"
              /> */}
                <path
                  strokeWidth="1px"
                  d="M17.6 6.2c-1.5-1.5-3.4-2.3-5.5-2.3-4.3 0-7.8 3.5-7.8 7.8 0 1.4.4 2.7 1 3.9l-1.1 4 4.1-1.1c1.1.6 2.4.9 3.7.9 4.3 0 7.8-3.5 7.8-7.8.1-2-.7-3.9-2.2-5.4zm-5.5 11.9c-1.2 0-2.3-.3-3.3-.9l-.2-.1-2.4.6.7-2.4-.2-.2c-.6-1-1-2.2-1-3.4 0-3.6 2.9-6.5 6.5-6.5 1.7 0 3.3.7 4.6 1.9 1.2 1.2 1.9 2.8 1.9 4.6-.1 3.5-3 6.4-6.6 6.4zm3.5-4.8c-.2-.1-1.1-.6-1.3-.6-.2-.1-.3-.1-.4.1-.1.2-.5.6-.6.8-.1.1-.2.1-.4 0s-.8-.3-1.6-1c-.6-.5-1-1.2-1.1-1.3-.1-.2 0-.3.1-.4l.3-.3s.1-.2.2-.3c.1-.1 0-.2 0-.3s-.4-1.1-.6-1.4c-.2-.4-.3-.3-.4-.3h-.4s-.3 0-.5.2-.7.7-.7 1.6c0 1 .7 1.9.8 2s1.4 2.1 3.3 2.9c.5.2.8.3 1.1.4.5.1.9.1 1.2.1.4-.1 1.1-.5 1.3-.9.2-.5.2-.8.1-.9 0-.2-.2-.3-.4-.4z"
                />
              </Icon>
            </div>
            {withText && `Share on WhatsApp`}
          </div>
        </a>
      )}

      {/* Sharingbutton Hacker News  */}
      {icons.includes(`hackernews`) && (
        <a
          href={`https://news.ycombinator.com/submitlink?u=${encodeURIComponent(
            uri
          )}&amp;t=${encodeURIComponent(message)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on Hacker News"
        >
          <div className={classNames(`inline-flex`)}>
            <div aria-hidden="true">
              <Icon
                className={classNames(
                  `text-white`,
                  bgClass
                    ? `${bgClass} hover:vivid-gradient`
                    : `bg-[#FF6600] hover:bg-[#FB6200]`,
                  className ? className : `w-8 h-8`,
                  circle ? `rounded-full` : `rounded-none`
                )}
                sourceSvgWidth={256}
                sourceSvgHeight={256}
              >
                {/* <circle cx="128" cy="128" r="122.5" /> */}
                <path
                  fillRule="evenodd"
                  strokeWidth="10px"
                  d="M128 256c70.692 0 128-57.308 128-128C256 57.308 198.692 0 128 0 57.308 0 0 57.308 0 128c0 70.692 57.308 128 128 128zm-9.06-113.686L75 60h20.08l25.85 52.093c.397.927.86 1.888 1.39 2.883.53.994.995 2.02 1.393 3.08.265.4.463.764.596 1.095.13.334.262.63.395.898.662 1.325 1.26 2.618 1.79 3.877.53 1.26.993 2.42 1.39 3.48 1.06-2.254 2.22-4.673 3.48-7.258 1.26-2.585 2.552-5.27 3.877-8.052L161.49 60h18.69l-44.34 83.308v53.087h-16.9v-54.08z"
                />
              </Icon>
            </div>
            {withText && `Share on Hacker News`}
          </div>
        </a>
      )}

      {/* Sharingbutton VK  */}
      {icons.includes(`vk`) && (
        <a
          href={`http://vk.com/share.php?title=${encodeURIComponent(
            message
          )}&amp;url=${encodeURIComponent(uri)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on VK"
        >
          <div className={classNames(`inline-flex`)}>
            <div aria-hidden="true">
              <Icon
                className={classNames(
                  `text-white`,
                  bgClass
                    ? `${bgClass} hover:vivid-gradient`
                    : `bg-[#507299] hover:bg-[#43648c]`,
                  className ? className : `w-8 h-8`,
                  circle ? `rounded-full` : `rounded-none`
                )}
                sourceSvgWidth={24}
                sourceSvgHeight={24}
              >
                {/* <circle cx="12" cy="12" r="11.5" /> */}
                <path
                  strokeWidth="1.5px"
                  d="M20.44 7.62h-2.9c-.24 0-.463.133-.577.347 0 0-1.156 2.13-1.528 2.848-1 1.932-1.647 1.326-1.647.43V8.152c0-.537-.436-.972-.973-.972h-2.182c-.604-.044-1.188.227-1.543.717 0 0 1.106-.18 1.106 1.313 0 .37.02 1.434.035 2.328.008.355-.274.65-.63.656-.187.003-.365-.074-.49-.213-.892-1.24-1.632-2.585-2.202-4.004-.1-.216-.316-.355-.556-.355H3.716c-.248 0-.45.198-.452.445 0 .055.01.108.028.16.8 2.195 4.25 9.094 8.185 9.094h1.655c.362 0 .654-.294.654-.655v-1c0-.356.29-.643.646-.643.162 0 .32.063.438.175l1.98 1.862c.18.166.413.26.658.26h2.604c1.255 0 1.255-.872.57-1.547-.48-.474-2.22-2.308-2.22-2.308-.307-.32-.336-.813-.07-1.166.563-.742 1.482-1.95 1.873-2.47.534-.707 1.498-2.21.176-2.21z"
                />
              </Icon>
            </div>
            {withText && `Share on VK`}
          </div>
        </a>
      )}

      {/* Sharingbutton Telegram  */}
      {icons.includes(`telegram`) && (
        <a
          href={`https://telegram.me/share/url?text=${encodeURIComponent(
            message
          )}&amp;url=${encodeURIComponent(uri)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on Telegram"
        >
          <div className={classNames(`inline-flex`)}>
            <div aria-hidden="true">
              <Icon
                className={classNames(
                  `text-white`,
                  bgClass
                    ? `${bgClass} hover:vivid-gradient`
                    : `bg-[#54A9EB] hover:bg-[#4B97D1]`,
                  className ? className : `w-8 h-8`,
                  circle ? `rounded-full` : `rounded-none`
                )}
                sourceSvgWidth={24}
                sourceSvgHeight={24}
              >
                {/* <circle cx="12" cy="12" r="11.5" /> */}
                <path d="M2.505 11.053c-.31.118-.505.738-.505.738s.203.62.513.737l3.636 1.355 1.417 4.557a.787.787 0 0 0 1.25.375l2.115-1.72a.29.29 0 0 1 .353-.01L15.1 19.85a.786.786 0 0 0 .746.095.786.786 0 0 0 .487-.573l2.793-13.426a.787.787 0 0 0-1.054-.893l-15.568 6z" />
              </Icon>
            </div>
            {withText && `Share on Telegram`}
          </div>
        </a>
      )}
    </div>
  )
}

export default SocialShareIcons
